import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Package, Zap, ListOrdered, ArrowRight } from 'lucide-react';
import axios from '../utils/axiosInstance';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

// Pretty labels for sync_type keys returned by /api/transly/sync-runs/.
// Kept in one place so a typo can't drift between the dashboard card and
// the activity page.
const SYNC_TYPE_LABELS = {
  daily_translate: 'Daily translate',
  product_resources: 'Products',
  collection: 'Collections',
  content_resource: 'Pages & articles',
  menu_link: 'Menus',
  theme: 'Theme strings',
  metafield: 'Metafields',
  metaobject: 'Metaobjects',
  filter_label: 'Filter labels',
};

function formatRelative(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  if (Number.isNaN(diff)) return iso;
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function RunStatusBadge({ status }) {
  const map = {
    success: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
    error: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    running: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  };
  const label = { success: 'OK', error: 'Failed', running: 'Running' }[status] || 'Unknown';
  const cls = map[status] || 'bg-muted text-muted-foreground';
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${cls}`}>
      {label}
    </span>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md">
      <p className="text-sm font-medium text-foreground mb-1">{label}</p>
      {payload.map((entry) => (
        <div key={entry.name} className="flex items-center gap-2 text-xs">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-muted-foreground">{entry.name}:</span>
          <span className="font-medium text-foreground">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [syncRuns, setSyncRuns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      try {
        // Sync-runs failure shouldn't blank the whole dashboard — fall back
        // to an empty list and let the stats render.
        const [statsRes, runsRes] = await Promise.all([
          axios.get('api/transly/stats/'),
          axios.get('api/transly/sync-runs/?limit_per_type=1').catch(() => ({ data: { runs: [] } })),
        ]);
        if (!cancelled) {
          setStats(statsRes.data);
          setSyncRuns(runsRes.data?.runs || []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="p-8 text-muted-foreground">Loading...</div>;
  if (error) return <div className="p-8 text-destructive">Error: {error}</div>;
  if (!stats) return null;

  const chartData = stats.resourceTypes.reduce((acc, item) => {
    let entry = acc.find((e) => e.locale === item.locale);
    if (!entry) {
      entry = { locale: item.locale };
      acc.push(entry);
    }
    entry[item.resourceType] = item.translated;
    return acc;
  }, []);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-foreground mb-6">Dashboard</h1>

      {/* Coverage by locale */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {stats.locales.map((loc) => (
          <Card key={loc.locale}>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle className="text-base uppercase">{loc.locale}</CardTitle>
                <span className={`text-2xl font-bold ${
                  loc.percentage >= 80 ? 'text-emerald-500' :
                  loc.percentage >= 50 ? 'text-yellow-500' : 'text-destructive'
                }`}>
                  {loc.percentage}%
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    loc.percentage >= 80 ? 'bg-emerald-500' :
                    loc.percentage >= 50 ? 'bg-yellow-500' : 'bg-destructive'
                  }`}
                  style={{ width: `${Math.min(loc.percentage, 100)}%` }}
                />
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {loc.translated} / {loc.total} resources
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Bar chart */}
      {chartData.length > 0 && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-base">Coverage by Resource Type</CardTitle>
            <CardDescription>Number of translated resources per locale</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="locale" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(var(--accent))' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="PRODUCT" fill="#3b82f6" name="Products" radius={[4, 4, 0, 0]} />
                <Bar dataKey="COLLECTION" fill="#10b981" name="Collections" radius={[4, 4, 0, 0]} />
                <Bar dataKey="PAGE" fill="#f59e0b" name="Pages" radius={[4, 4, 0, 0]} />
                <Bar dataKey="ARTICLE" fill="#8b5cf6" name="Articles" radius={[4, 4, 0, 0]} />
                <Bar dataKey="MENU" fill="#ec4899" name="Menus" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Recent sync activity (sync_runs audit log; nightly cron @ 03:00 UTC) */}
      <Card className="mb-8">
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">Recent sync activity</CardTitle>
            <CardDescription>
              Most recent run per resource type. Nightly cron runs at 03:00 UTC.
            </CardDescription>
          </div>
          <button
            onClick={() => navigate('/sync-activity')}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            Full activity <ArrowRight className="h-3 w-3" />
          </button>
        </CardHeader>
        <CardContent>
          {syncRuns && syncRuns.length > 0 ? (
            <div className="divide-y">
              {Object.keys(SYNC_TYPE_LABELS).map((syncType) => {
                // Backend orders by (sync_type ASC, started_at DESC); first
                // row per type is the latest run. find() handles types that
                // have never run (still rendered as "Never run").
                const run = syncRuns.find((r) => r.sync_type === syncType);
                return (
                  <div key={syncType} className="flex items-center justify-between py-2 text-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-medium text-foreground truncate">
                        {SYNC_TYPE_LABELS[syncType]}
                      </span>
                      {run ? (
                        <RunStatusBadge status={run.status} />
                      ) : (
                        <span className="inline-block text-xs font-medium px-2 py-0.5 rounded bg-muted text-muted-foreground">
                          Never run
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      {run && (run.new_count != null || run.deleted_count != null) && (
                        <span>
                          {run.new_count != null && (
                            <span className="text-emerald-600 dark:text-emerald-400">
                              +{run.new_count}
                            </span>
                          )}
                          {run.new_count != null && run.deleted_count != null && ' / '}
                          {run.deleted_count != null && (
                            <span className="text-red-600 dark:text-red-400">
                              -{run.deleted_count}
                            </span>
                          )}
                        </span>
                      )}
                      <span title={run?.finished_at || run?.started_at || ''}>
                        {formatRelative(run?.finished_at || run?.started_at)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No sync activity yet. The first nightly run lands at 03:00 UTC,
              or trigger a sync manually from any resource page.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { path: '/products', icon: Package, title: 'Products', desc: 'Browse and translate products' },
          { path: '/bulk', icon: Zap, title: 'Bulk Translate', desc: 'Translate everything at once' },
          { path: '/queue', icon: ListOrdered, title: 'Queue', desc: 'Monitor translation jobs' },
        ].map((item) => (
          <Card key={item.path} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(item.path)}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <item.icon className="h-4 w-4 text-primary" />
                </div>
                <CardTitle className="text-base">{item.title}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription>{item.desc}</CardDescription>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
